import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Clock, MapPin, Zap, Phone, CalendarCheck } from "lucide-react";
import { BookingForm } from "./BookingForm";
import { trackContact } from "../lib/track";

const HERO_IMG =
  "https://images.unsplash.com/photo-1532984601283-d68a3ee3e51f?crop=entropy&cs=srgb&fm=jpg&q=70&w=1600";

const LINES = [
  [{ t: "Reliable Taxi Near You" }],
  [{ t: "Book a Taxi Now — " }, { t: "24/7", gold: true }],
];

const TiltCard = ({ children }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(my, { stiffness: 150, damping: 22 });
  const ry = useSpring(mx, { stiffness: 150, damping: 22 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 5);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1100 }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {children}
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-20 pb-12">
      <motion.div className="absolute inset-0" style={{ y: bgY }} aria-hidden="true">
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-[120%] object-cover opacity-30"
          loading="eager"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink-950/70 via-ink/80 to-ink" aria-hidden="true" />
      <div
        className="absolute -top-32 right-[-10%] w-[540px] h-[540px] rounded-full opacity-25 blur-[120px]"
        style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)" }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center w-full">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-gold-300 mb-5"
          >
            Taxi near me — reliable Sydney taxi service
          </motion.p>

          <h1 data-testid="hero-title-h1" className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.75, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.map((seg, j) => (
                    <span key={j} className={seg.gold ? "text-gold-grad" : ""}>{seg.t}</span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-5 text-base sm:text-lg text-[#99A1B3] max-w-xl"
          >
            Need a ride? Local professional taxi drivers available 24/7. Fast pickup within
            minutes — fixed meter rates, zero surge, every Sydney suburb.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3.5"
          >
            <a
              href="tel:1300582485"
              onClick={trackContact}
              data-testid="hero-call-now-button"
              className="rounded-full bg-gold-400 text-ink font-bold px-8 py-4 flex items-center justify-center gap-2 hover:bg-gold-300 transition-colors duration-200"
            >
              <Phone size={18} /> Call Taxi Now
            </a>
            <a
              href="#book"
              data-testid="hero-book-cta"
              className="rounded-full border border-gold-400/40 text-gold-300 font-semibold px-8 py-4 flex items-center justify-center gap-2 hover:bg-gold-400/10 transition-colors duration-200"
            >
              <CalendarCheck size={18} /> Book Online Here
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 flex flex-wrap gap-x-7 gap-y-2.5 font-mono text-xs text-[#99A1B3]"
          >
            <span className="flex items-center gap-2"><Clock size={13} className="text-gold-400" /> 24/7 DISPATCH</span>
            <span className="flex items-center gap-2"><Zap size={13} className="text-gold-400" /> 3–7 MIN PICKUP</span>
            <span className="flex items-center gap-2"><MapPin size={13} className="text-gold-400" /> ALL SYDNEY SUBURBS</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
        >
          <TiltCard>
            <div className="glass border border-gold-400/25 rounded-3xl p-6 sm:p-8 gold-glow">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-300 mb-1">
                Instant booking
              </p>
              <h2 className="font-display text-xl font-extrabold text-white mb-6">
                Book a taxi near you — 30 seconds
              </h2>
              <BookingForm variant="compact" />
            </div>
          </TiltCard>
        </motion.div>
      </div>
    </section>
  );
};
